import React, { useState, useCallback } from "react";
import {
  Sparkles, FileText, CheckCircle2, ClipboardCopy, Loader2, Brain, ShieldCheck,
  Stethoscope, ListChecks, Paperclip, MailCheck, ChevronDown, AlertCircle, Play, RotateCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * One unified accent system for the entire denial intelligence section.
 *   ACCENT       — primary brand cyan (#14B8A6)
 *   ACCENT_DEEP  — deep teal used on dark surfaces & active states
 *   ACCENT_SOFT  — translucent cyan for tinted panels
 *   SLATE_*      — neutral surfaces & typography
 *   ALERT_*      — muted red / amber only, never decorative
 */
const ACCENT       = "#14B8A6";
const ACCENT_DEEP  = "#0F766E";
const ACCENT_BRIGHT = "#5EEAD4";
const SLATE_TXT    = "#F1F5F9";
const SLATE_MUTED  = "#B6C2D2";

// All 4 phases share the same accent — hierarchy comes from icon + label, not color.
const PHASES = [
  { key: "root_causes",      label: "Root Cause Analysis",       icon: Stethoscope, loadingText: "Analyzing denial root causes..." },
  { key: "action_plans",     label: "Action Plan",               icon: ListChecks,  loadingText: "Generating operational action plan..." },
  { key: "supporting_docs",  label: "Supporting Documentation",  icon: Paperclip,   loadingText: "Compiling supporting documentation..." },
  { key: "appeal_drafts",    label: "Appeal Draft",              icon: MailCheck,   loadingText: "Building appeal draft recommendations..." },
];

const PHASE_TIMEOUT_MS = 45000;

const ProbabilityRing = ({ value = null }) => {
  const v = value == null ? 0 : Math.max(0, Math.min(100, value));
  // Muted tones only: high = teal (good), low = amber (issue), unknown = slate
  const tone = value == null
    ? "rgba(148,163,184,0.6)"
    : v >= 60 ? ACCENT
    : v >= 30 ? "#F59E0B"
    : "#EF4444";
  const c = 2 * Math.PI * 22;
  const offset = c - (v / 100) * c;
  const glow = value != null && v >= 60 ? "drop-shadow(0 0 6px rgba(20,184,166,0.45))" : "none";
  return (
    <div className="relative h-14 w-14 shrink-0" style={{ filter: glow }}>
      <svg viewBox="0 0 52 52" className="h-14 w-14 -rotate-90">
        <circle cx="26" cy="26" r="22" stroke="rgba(255,255,255,0.06)" strokeWidth="3.5" fill="none" />
        <circle
          cx="26" cy="26" r="22"
          stroke={tone} strokeWidth="3.5" fill="none"
          strokeLinecap="round" strokeDasharray={c} strokeDashoffset={offset}
          style={{ transition: "stroke-dashoffset 700ms ease" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center" style={{ color: tone }}>
        <div className="text-[12px] font-bold leading-none tracking-tight">{value == null ? "—" : `${v}%`}</div>
        <div className="text-[7px] uppercase tracking-[0.16em] font-semibold mt-0.5 opacity-70">Recovery</div>
      </div>
    </div>
  );
};

const SubsectionHeader = ({ icon: Icon, label, status = "ready" }) => (
  <div className="flex items-center gap-1.5 mt-5 mb-2">
    <Icon className="h-3.5 w-3.5" style={{ color: status === "ready" ? ACCENT_BRIGHT : "rgba(182,194,210,0.85)" }} />
    <span
      className="text-[10px] font-semibold uppercase tracking-[0.16em]"
      style={{ color: status === "ready" ? "#E2E8F0" : "#CBD5E1" }}
    >
      {label}
    </span>
    {status === "loading" && <Loader2 className="h-3 w-3 animate-spin" style={{ color: ACCENT }} />}
  </div>
);

const PhaseLoadingPlaceholder = ({ text }) => (
  <div className="rounded-lg border border-white/5 bg-white/[0.02] px-3 py-2.5 flex items-center gap-2">
    <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" style={{ color: ACCENT }} />
    <span className="text-xs" style={{ color: SLATE_MUTED }}>{text}</span>
  </div>
);

const PhaseErrorPlaceholder = ({ text }) => (
  <div className="rounded-lg border border-amber-400/30 bg-amber-400/10 px-3 py-2.5 flex items-start gap-2">
    <AlertCircle className="h-3.5 w-3.5 text-amber-300 shrink-0 mt-0.5" />
    <span className="text-xs text-amber-200">{text}</span>
  </div>
);

// Polished idle state — feels like an AI-assistant hint, not a placeholder.
const PhaseIdlePlaceholder = ({ tip }) => (
  <div
    className="rounded-lg px-3 py-2.5 flex items-center gap-2.5"
    style={{
      background: "linear-gradient(90deg, rgba(20,184,166,0.04) 0%, rgba(255,255,255,0.015) 100%)",
      border: "1px dashed rgba(94,234,212,0.18)",
    }}
  >
    <span className="ai-pulse-dot" aria-hidden="true" />
    <span className="text-xs" style={{ color: "#CBD5E1" }}>{tip}</span>
  </div>
);

// Reusable panel — soft slate surface with a left teal hairline. Replaces the four
// different colored backgrounds with one cohesive look.
const ContentPanel = ({ children, dashed = false }) => (
  <div
    className={`rounded-lg px-3 py-2.5 ${dashed ? "border border-dashed border-white/10" : "border border-white/[0.06]"}`}
    style={{
      background: dashed ? "transparent" : "linear-gradient(180deg, rgba(20,184,166,0.04) 0%, rgba(255,255,255,0.02) 100%)",
      borderLeft: dashed ? undefined : `2px solid ${ACCENT_DEEP}`,
    }}
  >
    {children}
  </div>
);

// ---------- AI Operational Intelligence helpers ----------
const _fmtMoney = (n) => {
  if (n == null || isNaN(n)) return "$0";
  const v = Number(n);
  if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000) return `$${(v / 1_000).toFixed(0)}K`;
  return `$${Math.round(v).toLocaleString()}`;
};

const PHASE_LABELS = {
  root_causes:      "Root Cause Insight",
  action_plans:     "Operational Action Plan",
  supporting_docs:  "Recommended Supporting Documentation",
  appeal_drafts:    "Auto-Drafted Appeal Letter",
};

// One-line collapsed summary for each phase — surface intelligence without expansion.
const phaseSummary = (phaseKey, st, rc, ap, sd, ad) => {
  if (st === "loading") return "Generating intelligence…";
  if (st === "error")   return "Generation failed — retry available.";
  if (st === "idle")    return null;
  if (phaseKey === "root_causes")     return rc?.root_cause ? rc.root_cause.split(/\.|;/)[0] : "Operational root cause identified.";
  if (phaseKey === "action_plans")    return ap?.actions?.length ? `${ap.actions.length} AI-prioritized operational actions identified` : "Action plan generated.";
  if (phaseKey === "supporting_docs") return sd?.documents?.length ? `${sd.documents.length} recommended supporting documents` : "Documentation list compiled.";
  if (phaseKey === "appeal_drafts")   return ad?.appeal_strategy ? "Auto-drafted appeal ready for review" : "Appeal recommendation ready.";
  return null;
};

const cardStatusTag = (item, phases, ad) => {
  // Workflow-status communication: highest priority signal wins.
  const allReady = ["root_causes","action_plans","supporting_docs","appeal_drafts"].every((k) => phases[k]?.status === "ready");
  const anyError = ["root_causes","action_plans","supporting_docs","appeal_drafts"].some((k) => phases[k]?.status === "error");
  const anyLoading = ["root_causes","action_plans","supporting_docs","appeal_drafts"].some((k) => phases[k]?.status === "loading");
  if (anyLoading)  return { label: "Generating Intelligence", tone: "loading" };
  if (anyError)    return { label: "Review Needed",          tone: "warn" };
  if (allReady && (ad?.recovery_probability ?? 0) >= 70) return { label: "Appeal Draft Ready", tone: "ready" };
  if (allReady)    return { label: "Ready for Review",       tone: "ready" };
  if ((item.preventable_pct || 0) >= 60) return { label: "High Priority",  tone: "warn" };
  return { label: "Awaiting Analysis", tone: "idle" };
};

const STATUS_STYLES = {
  loading: { color: "#5EEAD4", bg: "rgba(20,184,166,0.10)", border: "rgba(94,234,212,0.40)" },
  ready:   { color: "#86EFAC", bg: "rgba(74,222,128,0.10)", border: "rgba(74,222,128,0.45)" },
  warn:    { color: "#FCD34D", bg: "rgba(251,191,36,0.10)", border: "rgba(251,191,36,0.45)" },
  idle:    { color: "#CBD5E1", bg: "rgba(148,163,184,0.10)", border: "rgba(148,163,184,0.30)" },
};

const StatusBadge = ({ label, tone }) => {
  const s = STATUS_STYLES[tone] || STATUS_STYLES.idle;
  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full"
      style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}` }}
    >
      {tone === "loading" && <Loader2 className="h-2.5 w-2.5 animate-spin" />}
      {tone === "ready" && <CheckCircle2 className="h-2.5 w-2.5" />}
      {tone === "warn" && <AlertCircle className="h-2.5 w-2.5" />}
      {label}
    </span>
  );
};

// Compact, dark AI-tone metric chip used in the expanded card KPI strip.
const IntelChip = ({ label, value, tone = "teal", testid }) => {
  const tones = {
    teal:    { color: "#5EEAD4", bg: "rgba(20,184,166,0.10)", border: "rgba(94,234,212,0.30)" },
    emerald: { color: "#86EFAC", bg: "rgba(74,222,128,0.10)", border: "rgba(74,222,128,0.30)" },
    amber:   { color: "#FCD34D", bg: "rgba(251,191,36,0.10)", border: "rgba(251,191,36,0.32)" },
    red:     { color: "#FCA5A5", bg: "rgba(248,113,113,0.10)", border: "rgba(248,113,113,0.32)" },
    slate:   { color: "#E2E8F0", bg: "rgba(148,163,184,0.06)", border: "rgba(148,163,184,0.22)" },
  };
  const t = tones[tone] || tones.slate;
  return (
    <div
      className="rounded-lg px-2.5 py-1.5 min-w-0 transition-all hover:-translate-y-0.5"
      style={{ background: t.bg, border: `1px solid ${t.border}` }}
      data-testid={testid}
    >
      <div className="text-[9px] uppercase tracking-[0.08em] font-semibold leading-tight opacity-80 break-words" style={{ color: t.color }}>{label}</div>
      <div className="text-sm font-display font-semibold tabular-nums mt-1 leading-tight" style={{ color: t.color }}>{value}</div>
    </div>
  );
};

// ---------- Phase Accordion (progressive disclosure inside each card) ----------
const PhaseAccordion = ({ phaseKey, icon: Icon, title, status, summary, defaultOpen = false, children, testIndex }) => {
  const [open, setOpen] = useState(defaultOpen);
  const interactive = status === "ready";
  return (
    <div className="rounded-lg overflow-hidden mt-2" style={{ background: "rgba(255,255,255,0.015)", border: "1px solid rgba(255,255,255,0.06)" }}>
      <button
        type="button"
        onClick={() => interactive && setOpen((v) => !v)}
        disabled={!interactive}
        data-testid={`phase-toggle-${phaseKey}-${testIndex}`}
        className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition-colors ${interactive ? "hover:bg-white/[0.04] focus:outline-none focus-visible:bg-white/[0.05]" : ""}`}
        aria-expanded={open}
      >
        <Icon className="h-3.5 w-3.5 shrink-0" style={{ color: status === "ready" ? ACCENT_BRIGHT : "rgba(94,234,212,0.55)" }} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10.5px] font-semibold uppercase tracking-[0.16em]" style={{ color: status === "ready" ? "#F1F5F9" : "#CBD5E1" }}>
              {title}
            </span>
            {status === "loading" && <Loader2 className="h-3 w-3 animate-spin" style={{ color: ACCENT }} />}
            {status === "ready" && <CheckCircle2 className="h-3 w-3" style={{ color: ACCENT_BRIGHT }} />}
            {status === "error" && <AlertCircle className="h-3 w-3 text-amber-300" />}
          </div>
          {summary && !open && (
            <div className="text-[11.5px] mt-0.5 leading-snug truncate" style={{ color: "#CBD5E1" }}>
              {summary}
            </div>
          )}
        </div>
        {interactive && (
          <ChevronDown
            className={`h-3.5 w-3.5 shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`}
            style={{ color: ACCENT_BRIGHT }}
          />
        )}
      </button>
      <div
        className="overflow-hidden transition-[max-height,opacity] duration-300 ease-out"
        style={{ maxHeight: open ? "1200px" : "0", opacity: open ? 1 : 0 }}
      >
        <div className="px-3 pb-3 pt-1">
          {children}
        </div>
      </div>
    </div>
  );
};

const AppealCard = ({ item, index, phases, onGenerate }) => {
  const [expanded, setExpanded] = useState(index === 0);
  const rc = phases.root_causes?.itemFor(item.denial_category);
  const ap = phases.action_plans?.itemFor(item.denial_category);
  const sd = phases.supporting_docs?.itemFor(item.denial_category);
  const ad = phases.appeal_drafts?.itemFor(item.denial_category);

  // --- AI-derived operational intelligence -------------------------------
  const recoveryProb = ad?.recovery_probability ?? null;
  const preventability = (item.preventable_pct || 0) >= 60 ? "High" : (item.preventable_pct || 0) >= 30 ? "Medium" : "Low";
  const operationalSeverity = (item.dollars || 0) >= 750_000 ? "Critical" : (item.dollars || 0) >= 250_000 ? "Elevated" : "Standard";
  const phasesReadyCount = ["root_causes","action_plans","supporting_docs","appeal_drafts"].filter((k) => phases[k]?.status === "ready").length;
  const aiConfidence = phasesReadyCount === 0 ? null : Math.min(95, 55 + phasesReadyCount * 10 + (recoveryProb ? Math.round(recoveryProb / 20) : 0));
  const estRecoverable = recoveryProb != null ? (item.dollars || 0) * (recoveryProb / 100) : null;
  const status = cardStatusTag(item, phases, ad);

  return (
    <div
      data-testid={`appeal-card-${index}`}
      className="ai-card rounded-xl p-5 transition-all duration-300 hover:shadow-[0_0_28px_-12px_rgba(20,184,166,0.55)]"
    >
      <div className="flex items-start gap-4">
        <ProbabilityRing value={recoveryProb} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-base font-semibold tracking-tight" style={{ color: "#FFFFFF" }}>{item.denial_category}</span>
            <span className="text-[10px] font-semibold tracking-wider uppercase px-1.5 py-0.5 rounded"
                  style={{ background: "rgba(20,184,166,0.16)", color: ACCENT_BRIGHT, border: `1px solid ${ACCENT_DEEP}55` }}>
              {item.payor}
            </span>
            <StatusBadge label={status.label} tone={status.tone} />
          </div>
          <div className="mt-1.5 flex items-center gap-2 text-xs flex-wrap">
            <span className="font-mono font-semibold tracking-tight" style={{ color: "#FFFFFF" }}>{item.denied_amount}</span>
            <span style={{ color: "rgba(148,163,184,0.55)" }}>·</span>
            <span style={{ color: "#CBD5E1" }}>Denied Amount</span>
            {item.volume != null && (
              <>
                <span style={{ color: "rgba(148,163,184,0.55)" }}>·</span>
                <span style={{ color: "#CBD5E1" }}>{item.volume.toLocaleString()} claims</span>
              </>
            )}
            {item.pct_of_total != null && (
              <>
                <span style={{ color: "rgba(148,163,184,0.55)" }}>·</span>
                <span style={{ color: "#CBD5E1" }}>{item.pct_of_total.toFixed(1)}% of denied $</span>
              </>
            )}
          </div>
        </div>
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-xs inline-flex items-center gap-1 shrink-0 transition-colors"
          style={{ color: expanded ? ACCENT_BRIGHT : SLATE_MUTED }}
          data-testid={`appeal-expand-${index}`}
        >
          {expanded ? "Collapse" : "Expand"}
          <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-300 ${expanded ? "rotate-180" : ""}`} />
        </button>
      </div>

      {expanded && (
        <div className="mt-4">
          {/* AI Operational Intelligence chip strip — the executive summary */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 mb-1" data-testid={`appeal-intel-strip-${index}`}>
            <IntelChip
              label="Recovery"
              value={recoveryProb != null ? `${recoveryProb}%` : "—"}
              tone={recoveryProb == null ? "slate" : recoveryProb >= 60 ? "teal" : "amber"}
              testid={`intel-recovery-prob-${index}`}
            />
            <IntelChip
              label="Recoverable"
              value={estRecoverable != null ? _fmtMoney(estRecoverable) : "—"}
              tone={estRecoverable && estRecoverable >= 250_000 ? "emerald" : "teal"}
              testid={`intel-recoverable-${index}`}
            />
            <IntelChip
              label="Preventability"
              value={preventability}
              tone={preventability === "High" ? "amber" : preventability === "Medium" ? "teal" : "slate"}
              testid={`intel-preventability-${index}`}
            />
            <IntelChip
              label="Severity"
              value={operationalSeverity}
              tone={operationalSeverity === "Critical" ? "red" : operationalSeverity === "Elevated" ? "amber" : "slate"}
              testid={`intel-severity-${index}`}
            />
            <IntelChip
              label="Recovery Confidence"
              value={aiConfidence != null ? `${aiConfidence}%` : "—"}
              tone={aiConfidence == null ? "slate" : aiConfidence >= 75 ? "emerald" : "teal"}
              testid={`intel-ai-confidence-${index}`}
            />
          </div>

          {/* Phase accordions — progressive disclosure */}
          <PhaseAccordion
            phaseKey="root_causes"
            icon={Stethoscope}
            title={PHASE_LABELS.root_causes}
            status={phases.root_causes?.status}
            summary={phaseSummary("root_causes", phases.root_causes?.status, rc, ap, sd, ad)}
            defaultOpen={true}
            testIndex={index}
          >
            {phases.root_causes?.status === "loading" && <PhaseLoadingPlaceholder text="Analyzing denial root causes…" />}
            {phases.root_causes?.status === "error"   && <PhaseErrorPlaceholder text={phases.root_causes.error || "Root cause analysis failed."} />}
            {phases.root_causes?.status === "ready" && rc?.root_cause && (
              <ContentPanel>
                <p className="text-xs leading-relaxed" style={{ color: SLATE_TXT }}>{rc.root_cause}</p>
                <div className="mt-2.5 flex flex-wrap items-center gap-1.5 pt-2.5 border-t border-white/5">
                  <span className="text-[9px] uppercase tracking-[0.14em] font-semibold" style={{ color: ACCENT_BRIGHT }}>Operational Signals</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: "rgba(20,184,166,0.08)", color: "#E2E8F0", border: "1px solid rgba(94,234,212,0.18)" }}>
                    Preventability: <span style={{ color: ACCENT_BRIGHT }}>{preventability}</span>
                  </span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: "rgba(20,184,166,0.08)", color: "#E2E8F0", border: "1px solid rgba(94,234,212,0.18)" }}>
                    Top Payor: <span style={{ color: ACCENT_BRIGHT }}>{item.payor}</span>
                  </span>
                  {item.preventable_pct != null && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: "rgba(20,184,166,0.08)", color: "#E2E8F0", border: "1px solid rgba(94,234,212,0.18)" }}>
                      Preventable Rate: <span style={{ color: ACCENT_BRIGHT }}>{item.preventable_pct.toFixed(0)}%</span>
                    </span>
                  )}
                </div>
              </ContentPanel>
            )}
            {phases.root_causes?.status === "idle" && (
              <PhaseIdlePlaceholder tip="Generate analysis to surface the operational root cause behind this denial driver." />
            )}
          </PhaseAccordion>

          <PhaseAccordion
            phaseKey="action_plans"
            icon={ListChecks}
            title={PHASE_LABELS.action_plans}
            status={phases.action_plans?.status}
            summary={phaseSummary("action_plans", phases.action_plans?.status, rc, ap, sd, ad)}
            testIndex={index}
          >
            {phases.action_plans?.status === "loading" && <PhaseLoadingPlaceholder text="Generating operational action plan…" />}
            {phases.action_plans?.status === "error"   && <PhaseErrorPlaceholder text={phases.action_plans.error || "Action plan failed."} />}
            {phases.action_plans?.status === "ready" && ap?.actions?.length > 0 && (
              <ContentPanel>
                <ol className="space-y-2">
                  {ap.actions.map((a, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-xs" style={{ color: SLATE_TXT }}>
                      <span className="inline-flex shrink-0 items-center justify-center h-4 w-4 rounded-full text-[10px] font-semibold mt-0.5"
                            style={{ background: ACCENT_DEEP, color: "#ECFEFF" }}>
                        {idx + 1}
                      </span>
                      <span className="leading-relaxed flex-1">{a}</span>
                      {idx === 0 && (
                        <span className="text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded shrink-0"
                              style={{ background: "rgba(251,191,36,0.12)", color: "#FCD34D", border: "1px solid rgba(251,191,36,0.32)" }}>
                          Highest Impact
                        </span>
                      )}
                    </li>
                  ))}
                </ol>
                <div className="mt-3 pt-2.5 border-t border-white/5 flex items-center gap-2 flex-wrap text-[10px]" style={{ color: "#CBD5E1" }}>
                  <span className="uppercase tracking-[0.14em] font-semibold" style={{ color: ACCENT_BRIGHT }}>Suggested Routing</span>
                  <span>→ Denial Recovery Queue · {item.payor} workgroup</span>
                </div>
              </ContentPanel>
            )}
            {phases.action_plans?.status === "idle" && (
              <PhaseIdlePlaceholder tip="Generate analysis to receive AI-prioritized operational corrective actions." />
            )}
          </PhaseAccordion>

          <PhaseAccordion
            phaseKey="supporting_docs"
            icon={Paperclip}
            title={PHASE_LABELS.supporting_docs}
            status={phases.supporting_docs?.status}
            summary={phaseSummary("supporting_docs", phases.supporting_docs?.status, rc, ap, sd, ad)}
            testIndex={index}
          >
            {phases.supporting_docs?.status === "loading" && <PhaseLoadingPlaceholder text="Compiling supporting documentation…" />}
            {phases.supporting_docs?.status === "error"   && <PhaseErrorPlaceholder text={phases.supporting_docs.error || "Supporting documentation failed."} />}
            {phases.supporting_docs?.status === "ready" && sd?.documents?.length > 0 && (
              <ContentPanel>
                <div className="flex items-center justify-between mb-2 pb-2 border-b border-white/5">
                  <span className="text-[10px] uppercase tracking-[0.14em] font-semibold" style={{ color: ACCENT_BRIGHT }}>
                    Documentation Completeness
                  </span>
                  <span className="text-[10px] font-semibold tabular-nums" style={{ color: "#86EFAC" }}>
                    {sd.documents.length} / {sd.documents.length} recommended
                  </span>
                </div>
                <ul className="grid sm:grid-cols-2 gap-x-3 gap-y-1.5">
                  {sd.documents.map((doc, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-xs" style={{ color: SLATE_TXT }}>
                      <CheckCircle2 className="h-3 w-3 shrink-0" style={{ color: ACCENT }} />
                      <span className="truncate" title={doc}>{doc}</span>
                    </li>
                  ))}
                </ul>
              </ContentPanel>
            )}
            {phases.supporting_docs?.status === "idle" && (
              <PhaseIdlePlaceholder tip="Generate analysis to compile payor-ready supporting documentation." />
            )}
          </PhaseAccordion>

          <PhaseAccordion
            phaseKey="appeal_drafts"
            icon={MailCheck}
            title={PHASE_LABELS.appeal_drafts}
            status={phases.appeal_drafts?.status}
            summary={phaseSummary("appeal_drafts", phases.appeal_drafts?.status, rc, ap, sd, ad)}
            testIndex={index}
          >
            {phases.appeal_drafts?.status === "loading" && <PhaseLoadingPlaceholder text="Building appeal draft recommendations…" />}
            {phases.appeal_drafts?.status === "error"   && <PhaseErrorPlaceholder text={phases.appeal_drafts.error || "Appeal draft failed."} />}
            {phases.appeal_drafts?.status === "ready" && (
              <div
                className="rounded-lg px-3 py-3"
                style={{
                  background: "linear-gradient(135deg, rgba(20,184,166,0.08) 0%, rgba(15,118,110,0.04) 100%)",
                  border: `1px solid ${ACCENT_DEEP}33`,
                }}
              >
                {ad?.appeal_strategy && (
                  <p className="text-xs leading-relaxed mb-3" style={{ color: SLATE_TXT }}>{ad.appeal_strategy}</p>
                )}
                <div className="flex items-center gap-3 flex-wrap">
                  <Sparkles className="h-4 w-4 shrink-0" style={{ color: ACCENT_BRIGHT }} />
                  <div className="flex-1 min-w-[200px] text-xs leading-tight" style={{ color: SLATE_MUTED }}>
                    Generate the full payor-specific appeal letter with denial-specific ANSI/CARC guidance.
                  </div>
                  <Button
                    data-testid={`generate-appeal-draft-${index}`}
                    onClick={() => onGenerate(item, rc)}
                    size="sm"
                    className="text-white text-xs h-9 rounded-lg shrink-0 transition-all hover:shadow-[0_0_20px_-6px_rgba(20,184,166,0.7)]"
                    style={{ background: ACCENT_DEEP }}
                  >
                    <FileText className="h-3.5 w-3.5 mr-1.5" /> Generate Appeal Draft
                  </Button>
                </div>
              </div>
            )}
            {phases.appeal_drafts?.status === "idle" && (
              <PhaseIdlePlaceholder tip="Generate analysis to receive an AI-assisted, payor-aware appeal draft." />
            )}
          </PhaseAccordion>
        </div>
      )}
    </div>
  );
};

export const AppealRecommendations = ({ topDenials = [], topPayor = "Top Payor" }) => {
  const [workflowStatus, setWorkflowStatus] = useState("idle");
  const [open, setOpen] = useState(false);
  const [phaseStates, setPhaseStates] = useState(() =>
    Object.fromEntries(PHASES.map((p) => [p.key, { status: "idle", items: [], error: null }]))
  );
  const [active, setActive] = useState(null);
  const [draftLoading, setDraftLoading] = useState(false);
  const [draft, setDraft] = useState(null);

  const items = topDenials.slice(0, 5).map((d) => ({
    denial_category: d.category,
    payor: topPayor,
    denied_amount: `$${Math.round(d.dollars).toLocaleString()}`,
    dollars: d.dollars,
    volume: d.volume,
    preventable_pct: d.preventable_pct,
    pct_of_total: d.pct_of_total,
  }));

  const phasesView = Object.fromEntries(
    PHASES.map((p) => {
      const st = phaseStates[p.key];
      return [
        p.key,
        { ...st, itemFor: (cat) => (st.items || []).find((i) => i.denial_category === cat) },
      ];
    })
  );

  const fetchPhase = useCallback(async (phaseKey) => {
    const controller = new AbortController();
    const t = setTimeout(() => controller.abort(), PHASE_TIMEOUT_MS);
    try {
      const { data } = await api.get(`/workflow/${phaseKey}`, { signal: controller.signal });
      clearTimeout(t);
      return data;
    } catch (err) {
      clearTimeout(t);
      throw err;
    }
  }, []);

  const runWorkflow = useCallback(async (forceRefresh = false) => {
    if (items.length === 0) { toast.error("No dataset loaded."); return; }
    setWorkflowStatus("running");
    if (forceRefresh) {
      try { await api.delete("/workflow/cache"); } catch (e) { /* ignore */ }
    }
    setPhaseStates(() => Object.fromEntries(PHASES.map((p) => [p.key, { status: "idle", items: [], error: null }])));
    for (const phase of PHASES) {
      setPhaseStates((prev) => ({ ...prev, [phase.key]: { ...prev[phase.key], status: "loading" } }));
      try {
        const data = await fetchPhase(phase.key);
        setPhaseStates((prev) => ({
          ...prev,
          [phase.key]: { status: "ready", items: data.items || [], error: null, cached: data._cached, fallback: data._fallback },
        }));
      } catch (err) {
        const msg = err?.name === "AbortError" || err?.code === "ERR_CANCELED"
          ? "Generation took too long. Try again in a moment."
          : (err?.response?.data?.detail || "Generation failed.");
        setPhaseStates((prev) => ({ ...prev, [phase.key]: { status: "error", items: [], error: msg } }));
        toast.error(`${phase.label}: ${msg}`);
      }
    }
    setWorkflowStatus("done");
  }, [items.length, fetchPhase]);

  const openDraft = async (item, rcItem) => {
    setActive(item);
    setDraft(null);
    setDraftLoading(true);
    try {
      const { data } = await api.post("/appeal/draft", {
        denial_category: item.denial_category,
        payor: item.payor,
        denied_amount: item.denied_amount,
        root_cause: rcItem?.root_cause || "",
      });
      setDraft(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to generate appeal draft");
      setActive(null);
    } finally {
      setDraftLoading(false);
    }
  };

  const closeDraft = () => { setActive(null); setDraft(null); };
  const copyLetter = () => {
    if (!draft?.letter) return;
    navigator.clipboard.writeText(draft.letter);
    toast.success("Letter copied to clipboard");
  };
  const downloadLetter = () => {
    if (!draft) return;
    const blob = new Blob([draft.letter], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `appeal_${(active?.denial_category || "letter").replace(/\s+/g, "_")}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const activePhase = PHASES.find((p) => phaseStates[p.key]?.status === "loading");

  // Auto-open the section once analysis is running or done so the cards become visible.
  React.useEffect(() => {
    if (workflowStatus === "running" || workflowStatus === "done") setOpen(true);
  }, [workflowStatus]);

  // Summary chips for the collapsed header — communicate value without expansion.
  const summary = (() => {
    const counts = PHASES.map((p) => phaseStates[p.key]?.status).filter((s) => s === "ready").length;
    const totalDollars = items.reduce((acc, it) => acc + (it.dollars || 0), 0);
    return { ready: counts, totalDollars };
  })();

  return (
    <>
      <div
        className="ai-shell ai-shell-edge rounded-2xl overflow-hidden"
        data-testid="appeal-recommendations-section"
      >
        {/* Header */}
        <div className="p-6 pb-4 border-b" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div
              role="button"
              tabIndex={0}
              onClick={() => setOpen((v) => !v)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen((v) => !v); } }}
              aria-expanded={open}
              data-testid="appeal-recommendations-toggle"
              className="flex-1 min-w-0 cursor-pointer rounded-lg -m-1 p-1 hover:bg-white/[0.02] focus:outline-none focus-visible:bg-white/[0.03] transition-colors"
            >
              <div className="flex items-center gap-2">
                <div className="h-7 w-7 rounded-md flex items-center justify-center"
                     style={{ background: "rgba(20,184,166,0.10)", border: `1px solid ${ACCENT_DEEP}55` }}>
                  <Brain className="h-3.5 w-3.5" style={{ color: ACCENT_BRIGHT }} />
                </div>
                <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.18em]" style={{ color: "#CBD5E1" }}>
                  <span className="ai-pulse-dot" aria-hidden="true" /> Denial Workflow
                </span>
              </div>
              <h2 className="font-display text-2xl sm:text-[26px] font-semibold tracking-tight mt-2" style={{ color: "#FFFFFF" }}>
                Denial Intelligence & Recovery Workflow
              </h2>
              <p className="text-xs mt-2 leading-relaxed max-w-2xl" style={{ color: "#E2E8F0" }}>
                Intelligent denial operations platform for root-cause intelligence, recovery prioritization, and operationally guided appeal workflows.
              </p>
              <p className="text-[11px] mt-1 leading-relaxed flex items-center gap-1.5" style={{ color: "#94A3B8" }}>
                <Sparkles className="h-2.5 w-2.5" style={{ color: ACCENT_BRIGHT }} />
                Powered by recoverability modeling and ANSI/CARC trend intelligence
              </p>
              {/* Inline status chips — visible even when collapsed */}
              <div className="mt-3 flex flex-wrap items-center gap-1.5">
                <span className="text-[10.5px] font-semibold px-2 py-0.5 rounded-full"
                      style={{ background: "rgba(20,184,166,0.10)", color: ACCENT_BRIGHT, border: `1px solid ${ACCENT_DEEP}55` }}>
                  {items.length} top driver{items.length === 1 ? "" : "s"} loaded
                </span>
                {workflowStatus === "done" && (
                  <span className="text-[10.5px] font-semibold px-2 py-0.5 rounded-full"
                        style={{ background: "rgba(74,222,128,0.10)", color: "#86EFAC", border: "1px solid rgba(74,222,128,0.30)" }}>
                    {summary.ready} / {PHASES.length} phases ready
                  </span>
                )}
                {workflowStatus === "running" && (
                  <span className="text-[10.5px] font-semibold px-2 py-0.5 rounded-full inline-flex items-center gap-1"
                        style={{ background: "rgba(20,184,166,0.10)", color: ACCENT_BRIGHT, border: `1px solid ${ACCENT_DEEP}55` }}>
                    <Loader2 className="h-2.5 w-2.5 animate-spin" /> Generating intelligence
                  </span>
                )}
                {workflowStatus === "idle" && (
                  <span className="text-[10.5px] font-semibold px-2 py-0.5 rounded-full"
                        style={{ background: "rgba(148,163,184,0.10)", color: "#CBD5E1", border: "1px solid rgba(148,163,184,0.30)" }}>
                    Awaiting analysis
                  </span>
                )}
              </div>
            </div>
            <div className="flex flex-col items-end gap-2 shrink-0">
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-full font-semibold"
                     style={{ background: "rgba(20,184,166,0.12)", color: ACCENT_BRIGHT, border: `1px solid ${ACCENT_DEEP}55` }}>
                  <ShieldCheck className="h-3 w-3" />
                  PHI-Free Appeal Drafting
                </div>
                <button
                  type="button"
                  onClick={() => setOpen((v) => !v)}
                  aria-label={open ? "Collapse workflow" : "Expand workflow"}
                  data-testid="appeal-recommendations-chevron"
                  className="h-8 w-8 rounded-full flex items-center justify-center transition-transform duration-300"
                  style={{ background: "rgba(20,184,166,0.12)", border: `1px solid ${ACCENT_DEEP}55`, transform: open ? "rotate(180deg)" : "rotate(0)" }}
                >
                  <ChevronDown className="h-4 w-4" style={{ color: ACCENT_BRIGHT }} />
                </button>
              </div>
              <p className="text-[10px] leading-tight text-right max-w-[220px]" style={{ color: "#94A3B8" }}>
                Designed for secure non-PHI operational analysis workflows.
              </p>
              {workflowStatus === "idle" && items.length > 0 && (
                <Button
                  data-testid="generate-analysis-button"
                  onClick={() => { setOpen(true); runWorkflow(false); }}
                  className="ai-cta rounded-lg px-5"
                  size="sm"
                >
                  <Play className="h-3.5 w-3.5 mr-1.5" /> Generate AI Recovery Analysis
                </Button>
              )}
              {workflowStatus === "running" && (
                <div className="flex items-center gap-1.5 text-xs" style={{ color: SLATE_MUTED }}>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" style={{ color: ACCENT }} />
                  <span>{activePhase ? activePhase.loadingText : "Working..."}</span>
                </div>
              )}
              {workflowStatus === "done" && (
                <Button
                  data-testid="regenerate-analysis-button"
                  onClick={() => runWorkflow(true)}
                  variant="outline"
                  className="rounded-lg"
                  size="sm"
                  style={{ borderColor: `${ACCENT_DEEP}66`, color: ACCENT_BRIGHT, background: "transparent" }}
                >
                  <RotateCw className="h-3.5 w-3.5 mr-1.5" /> Regenerate
                </Button>
              )}
            </div>
          </div>

          {/* Phase legend — only when section is open */}
          {open && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-6">
              {PHASES.map((p) => {
                const st = phaseStates[p.key]?.status || "idle";
                return (
                  <div key={p.key} className="flex items-center gap-1.5 text-[11px]" style={{ color: "#CBD5E1" }}>
                    {st === "loading" ? (
                      <Loader2 className="h-3 w-3 animate-spin" style={{ color: ACCENT_BRIGHT }} />
                    ) : st === "ready" ? (
                      <CheckCircle2 className="h-3 w-3" style={{ color: ACCENT_BRIGHT }} />
                    ) : st === "error" ? (
                      <AlertCircle className="h-3 w-3 text-amber-400" />
                    ) : (
                      <p.icon className="h-3 w-3" style={{ color: "rgba(94,234,212,0.7)" }} />
                    )}
                    <span className="font-medium" style={{ color: st === "ready" ? "#FFFFFF" : "#CBD5E1" }}>
                      {p.label}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Cards — collapsible body */}
        <div
          className="overflow-hidden transition-[max-height,opacity] duration-500 ease-out"
          style={{ maxHeight: open ? "8000px" : "0", opacity: open ? 1 : 0 }}
        >
          <div className="p-6 grid lg:grid-cols-2 gap-4" data-testid="appeal-recommendations-body">
            {items.length === 0 && (
              <div className="col-span-full text-sm py-8 text-center" style={{ color: SLATE_MUTED }}>
                No dataset loaded. Upload data or load a demo dataset to begin.
              </div>
            )}
            {items.map((item, i) => (
              <AppealCard key={i} item={item} index={i} phases={phasesView} onGenerate={openDraft} />
            ))}
          </div>
        </div>
      </div>

      {/* Appeal Draft Dialog */}
      <Dialog open={!!active} onOpenChange={(o) => { if (!o) closeDraft(); }}>
        <DialogContent
          className="max-w-3xl max-h-[88vh] overflow-y-auto"
          data-testid="appeal-draft-dialog"
          style={{
            background: "linear-gradient(180deg, #0A1626 0%, #0F2238 100%)",
            border: "1px solid rgba(255,255,255,0.08)",
            color: SLATE_TXT,
          }}
        >
          <DialogHeader>
            <div className="flex items-center gap-2 mb-1">
              <div className="h-7 w-7 rounded-md flex items-center justify-center"
                   style={{ background: "rgba(20,184,166,0.14)", border: `1px solid ${ACCENT_DEEP}55` }}>
                <MailCheck className="h-3.5 w-3.5" style={{ color: ACCENT_BRIGHT }} />
              </div>
              <span className="text-[10px] font-semibold uppercase tracking-[0.18em]" style={{ color: ACCENT_BRIGHT }}>
                AI Appeal Draft
              </span>
            </div>
            <DialogTitle className="font-display text-2xl" style={{ color: "#F1F5F9" }}>
              {draft?.subject || (active ? `Appeal of ${active.denial_category} — ${active.payor}` : "Appeal Draft")}
            </DialogTitle>
            <DialogDescription className="text-xs flex items-center gap-1.5" style={{ color: SLATE_MUTED }}>
              <ShieldCheck className="h-3 w-3" style={{ color: ACCENT }} />
              Generated with placeholder fields only. Replace bracketed values before sending.
            </DialogDescription>
          </DialogHeader>

          {draftLoading && (
            <div className="py-16 flex flex-col items-center justify-center" style={{ color: SLATE_MUTED }}>
              <Loader2 className="h-8 w-8 animate-spin mb-3" style={{ color: ACCENT }} />
              <div className="text-sm">Drafting payor-specific appeal letter...</div>
            </div>
          )}

          {!draftLoading && draft && (
            <div className="space-y-5">
              <div className="rounded-lg border border-amber-400/30 bg-amber-400/10 px-3 py-2 flex items-start gap-2">
                <AlertCircle className="h-3.5 w-3.5 text-amber-300 mt-0.5 shrink-0" />
                <div className="text-[11px] text-amber-200">
                  <strong>Placeholders used:</strong> [PATIENT_NAME], [MEMBER_ID], [AUTH_NUMBER], [DATE_OF_SERVICE],
                  [CLAIM_NUMBER], [PROVIDER_NAME], [NPI], [TAX_ID], [CONTACT_PHONE], [CONTACT_EMAIL]
                </div>
              </div>

              <div className="rounded-xl p-5"
                   style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }}>
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.14em]" style={{ color: ACCENT_BRIGHT }}>
                    Appeal Draft
                  </div>
                  <div className="flex items-center gap-2">
                    <Button onClick={copyLetter} variant="outline" size="sm"
                            className="text-xs h-8 rounded-md"
                            style={{ borderColor: `${ACCENT_DEEP}66`, color: ACCENT_BRIGHT, background: "transparent" }}
                            data-testid="copy-letter-button">
                      <ClipboardCopy className="h-3 w-3 mr-1" /> Copy
                    </Button>
                    <Button onClick={downloadLetter} size="sm"
                            className="text-white text-xs h-8 rounded-md"
                            style={{ background: ACCENT_DEEP }}
                            data-testid="download-letter-button">
                      <FileText className="h-3 w-3 mr-1" /> Download
                    </Button>
                  </div>
                </div>
                <pre className="text-xs whitespace-pre-wrap font-mono leading-relaxed" style={{ color: SLATE_TXT }}>
                  {draft.letter}
                </pre>
              </div>

              {draft.supporting_documents?.length > 0 && (
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-[0.14em] mb-2" style={{ color: ACCENT_BRIGHT }}>
                    Supporting Documents to Attach
                  </div>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {draft.supporting_documents.map((d, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs rounded-lg px-3 py-2"
                           style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", color: SLATE_TXT }}>
                        <CheckCircle2 className="h-3.5 w-3.5 shrink-0" style={{ color: ACCENT }} />
                        <span>{d}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {draft.next_steps?.length > 0 && (
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-[0.14em] mb-2" style={{ color: ACCENT_BRIGHT }}>
                    Next Steps
                  </div>
                  <ol className="space-y-2">
                    {draft.next_steps.map((s, i) => (
                      <li key={i} className="flex items-start gap-2 text-xs" style={{ color: SLATE_TXT }}>
                        <span className="inline-flex items-center justify-center h-4 w-4 rounded-full text-[10px] font-semibold shrink-0"
                              style={{ background: ACCENT_DEEP, color: "#ECFEFF" }}>
                          {i + 1}
                        </span>
                        <span>{s}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              {draft._fallback && (
                <div className="text-[11px] rounded-lg px-3 py-2 border border-amber-400/30 bg-amber-400/10 text-amber-200">
                  Using template draft (AI service unavailable). The letter is fully usable with placeholder fields.
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AppealRecommendations;
;
